// The Floating-Price Formula window (ledger row 132, Phil's mockup; AC15,
// AC16, AC19, AC20, AC22, AC25).
//
// The trader who agreed the price writes its formula here (Option A, Chris
// 2026-09-21). The window never computes a price: every change is sent to the
// server's preview (which uses the one calculator, ``floating_price.py``) and
// the answer is shown as the server gave it -- the formula in words, each
// step, the price, and whether it is final. A formula that cannot be priced
// yet shows the reason and NO number; a 0.00 there would read as a price.
//
// Nothing here is guessed on the trader's behalf. The conversion factor is
// never pre-filled (house rule: no invented densities), and premium or
// discount, the pricing basis and the month are all asked, not defaulted --
// on the live book a discount of USD 180/MT is normal, so a quietly assumed
// premium would be a USD 360/MT mistake. The one default is the price point,
// taken from the benchmark master's own "default price point".

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import AmountInput from '../../ui/AmountInput';
import { PRICE_DECIMALS } from '../../../utils/precision';
import { DatePicker } from '../../ui';
import floatingFormulaService from '../../../services/floatingFormulaService';
import {
  PRICING_OPTIONS,
  SIDE_LABEL,
  finalPriceLabel,
  firstOfMonth,
  monthOptions,
  normaliseUnit,
  priceText,
  pricePointsFor,
  resultHasPrice,
  serverSentence,
} from './floatingFormulaText';
import './FloatingFormula.css';

// Long enough that typing "180" asks once, short enough to feel live.
export const PREVIEW_DELAY_MS = 400;

const SIGN_OPTIONS = [
  { value: 'premium', label: 'Premium (+)' },
  { value: 'discount', label: 'Discount (-)' },
];

const draftFrom = (formula) => ({
  benchmarkId: formula?.benchmark?.id != null ? String(formula.benchmark.id) : '',
  bate: formula?.bate || '',
  pricingOption: formula?.pricing_option || '',
  periodMonth: firstOfMonth(formula?.period_month),
  pricingDate: formula?.pricing_date || '',
  periodStart: formula?.period_start || '',
  periodEnd: formula?.period_end || '',
  adjustmentSign: formula?.adjustment_sign || '',
  adjustmentValue: formula?.adjustment_value ?? '',
  adjustmentUnit: formula?.adjustment_unit || '',
  conversionFactor: formula?.conversion_factor ?? '',
});

/**
 * The exact body of ``PUT`` / ``POST preview`` (BUILD_CONTRACT_132 B).
 *
 * Fields belonging to a pricing option the trader did not pick go out as
 * null, so switching from a whole month to a date range can never leave a
 * stale month behind in the saved formula.
 */
export const formulaBody = (draft, { productId, side, unitsDiffer }) => {
  const option = draft.pricingOption;
  return {
    product_id: productId ?? null,
    side,
    benchmark_id: draft.benchmarkId === '' ? null : Number(draft.benchmarkId),
    bate: draft.bate || null,
    pricing_option: option || null,
    period_month: option === 'whole_month' && draft.periodMonth ? draft.periodMonth : null,
    pricing_date: option === 'single_day' && draft.pricingDate ? draft.pricingDate : null,
    period_start: option === 'date_range' && draft.periodStart ? draft.periodStart : null,
    period_end: option === 'date_range' && draft.periodEnd ? draft.periodEnd : null,
    adjustment_sign: draft.adjustmentSign || null,
    adjustment_value: draft.adjustmentValue === '' ? null : draft.adjustmentValue,
    // With one unit on both sides, "per benchmark unit" and "per deal unit"
    // are the same number, so the window does not ask; the server still wants
    // one of its two words.
    adjustment_unit: unitsDiffer ? (draft.adjustmentUnit || null) : 'deal',
    conversion_factor: unitsDiffer && draft.conversionFactor !== '' ? draft.conversionFactor : null,
  };
};

const Step = ({ number, title, children }) => (
  <section className="ff-step">
    <h4 className="ff-step-heading">
      <span className="ff-step-number">{number}</span> {title}
    </h4>
    {children}
  </section>
);

// Same control family as the enquiry's Fixed | Floating toggle.
const Segmented = ({ id, label, options, value, onChange, disabled }) => (
  <div className="ff-choice">
    <span className="fef-label" id={id}>
      {label} <span className="fef-label-required">*</span>
    </span>
    <div className="fef-pricing-toggle ff-segmented" role="group" aria-labelledby={id}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`fef-pricing-toggle-btn${value === opt.value ? ' is-active' : ''}`}
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
          disabled={disabled}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const ReadOnlyValue = ({ label, value, mono = false }) => (
  <div className="fef-field">
    <span className="fef-label">{label}</span>
    <div className={`fef-input-readonly${mono ? ' ff-mono' : ''}`}>{value || '—'}</div>
  </div>
);

const benchmarkOptionText = (b) => {
  const where = b.location ? ` · ${b.location}` : '';
  const text = `${b.code} — ${b.name}${where}`;
  return b.supported === false ? `${text} (not available: ${b.unsupported_reason})` : text;
};

export const FormulaPreview = ({ preview }) => {
  const { result } = preview;
  return (
    <section className="ff-preview" aria-live="polite">
      <div className="ff-preview-heading">
        Formula preview
        {preview.working && (
          <span className="ff-preview-working">
            {' '}<FontAwesomeIcon icon={faSpinner} spin /> working out the price…
          </span>
        )}
      </div>
      {preview.status === 'idle' && (
        <p className="ff-muted">Pick a benchmark to see the formula and its price.</p>
      )}
      {preview.status === 'failed' && (
        <p className="ff-preview-problem" role="alert">
          The price could not be worked out: {preview.error}
        </p>
      )}
      {preview.status === 'ready' && result && (
        <>
          {result.formula_text && <div className="ff-formula-text">{result.formula_text}</div>}
          {result.explanation && <p className="ff-explanation">{result.explanation}</p>}
          {resultHasPrice(result) ? (
            <>
              {Array.isArray(result.steps) && result.steps.length > 0 && (
                <dl className="ff-calc-steps">
                  {result.steps.map((step, idx) => (
                    <div className="ff-calc-step" key={`${step.key}-${idx}`}>
                      <dt>{step.label}</dt>
                      <dd>{step.value} {step.unit}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <div className={`ff-final ff-final--${result.status}`}>
                <span className="ff-final-label">{finalPriceLabel(result)}</span>
                <span className="ff-final-value" data-testid="ff-final-price">
                  {priceText(result)}
                </span>
              </div>
              <p className="ff-final-message">{result.message}</p>
            </>
          ) : (
            <p className="ff-no-price" role="status">{result.message}</p>
          )}
        </>
      )}
    </section>
  );
};

/**
 * @param {number} enquiryId
 * @param {string} displayId      "ENQ-0147"
 * @param {'sell'|'buy'} side
 * @param {number|null} productId null on an enquiry without product rows
 * @param {string} lineLabel      "Product 1 · VLSFO RMG 380"
 * @param {string|null} quantity  "500 MT"
 * @param {string} dealUnit       the line's unit code from the server ("MT")
 * @param {object|null} formula   the saved formula, null for a new one
 * @param {object|null} initialResult the saved formula's result, shown until
 *                                the first preview answers
 * @param {boolean} canEdit
 */
const FloatingFormulaModal = ({
  enquiryId,
  displayId,
  side,
  productId,
  lineLabel,
  quantity,
  dealUnit,
  formula,
  initialResult = null,
  canEdit,
  onClose,
  onSaved,
  onRemoved,
}) => {
  const [draft, setDraft] = useState(() => draftFrom(formula));
  const [benchmarks, setBenchmarks] = useState({ status: 'loading', list: [], error: null });
  const [preview, setPreview] = useState(() => (
    formula && initialResult
      ? { status: 'ready', result: initialResult, error: null, working: false }
      : { status: 'idle', result: null, error: null, working: false }
  ));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const latestPreview = useRef(0);

  const busy = saving || removing;
  const locked = !canEdit || busy;

  const loadBenchmarks = useCallback(async () => {
    setBenchmarks((prev) => ({ ...prev, status: 'loading', error: null }));
    try {
      const data = await floatingFormulaService.benchmarks();
      setBenchmarks({ status: 'ready', list: data.results, error: null });
    } catch (err) {
      setBenchmarks({
        status: 'failed',
        list: [],
        error: serverSentence(err, 'The benchmark list could not be loaded.'),
      });
    }
  }, []);

  useEffect(() => {
    loadBenchmarks();
  }, [loadBenchmarks]);

  // The list holds ACTIVE benchmarks only. A saved formula on a benchmark
  // that has since been switched off keeps it (AC4: formulas already saved
  // keep calculating), so the window still shows what the formula says.
  const savedBenchmark = formula?.benchmark || null;
  const selected = useMemo(() => {
    if (draft.benchmarkId === '') return null;
    const listed = benchmarks.list.find((b) => String(b.id) === draft.benchmarkId);
    if (listed) return listed;
    if (savedBenchmark && String(savedBenchmark.id) === draft.benchmarkId) {
      return { ...savedBenchmark, location: '', default_bate: formula.bate };
    }
    return null;
  }, [draft.benchmarkId, benchmarks.list, savedBenchmark, formula]);
  const savedIsListed = !savedBenchmark
    || benchmarks.list.some((b) => String(b.id) === String(savedBenchmark.id));

  const benchmarkUnit = normaliseUnit(selected?.unit);
  const lineUnit = normaliseUnit(dealUnit);
  const unitsDiffer = Boolean(selected) && Boolean(lineUnit) && benchmarkUnit !== lineUnit;
  const currency = selected?.currency || '';

  const body = useMemo(
    () => formulaBody(draft, { productId, side, unitsDiffer }),
    [draft, productId, side, unitsDiffer],
  );
  const bodyKey = JSON.stringify(body);

  // Live preview, debounced. Only the newest answer is shown: an older one
  // landing late must not replace the formula the trader is looking at.
  useEffect(() => {
    const request = latestPreview.current + 1;
    latestPreview.current = request;
    const payload = JSON.parse(bodyKey);
    if (payload.benchmark_id == null) {
      setPreview({ status: 'idle', result: null, error: null, working: false });
      return undefined;
    }
    const timer = setTimeout(async () => {
      setPreview((prev) => ({ ...prev, working: true }));
      try {
        const data = await floatingFormulaService.preview(enquiryId, payload);
        if (request !== latestPreview.current) return;
        setPreview({ status: 'ready', result: data.result, error: null, working: false });
      } catch (err) {
        if (request !== latestPreview.current) return;
        setPreview({
          status: 'failed',
          result: null,
          error: serverSentence(err, 'The server did not answer.'),
          working: false,
        });
      }
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [bodyKey, enquiryId]);

  const setField = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }));

  // A benchmark carries its usual price point (Close for nearly every
  // contract); picking one starts there, and the trader can still change it.
  const pickBenchmark = (id) => {
    const picked = benchmarks.list.find((b) => String(b.id) === id);
    setDraft((prev) => ({ ...prev, benchmarkId: id, bate: picked ? picked.default_bate : prev.bate }));
  };

  const handleSave = async () => {
    if (busy) return;
    if (unitsDiffer && draft.conversionFactor === '') {
      // Same words as the server's refusal (``refusal_for``), said before
      // the round trip rather than after it.
      setError(
        `${selected.code} is quoted per ${benchmarkUnit} and this deal is in ${lineUnit}. `
        + `Enter the contract conversion factor: how many ${benchmarkUnit} in one ${lineUnit}.`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = await floatingFormulaService.save(enquiryId, body);
      onSaved(data);
    } catch (err) {
      setError(serverSentence(err, 'The floating formula could not be saved. Please try again.'));
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (busy) return;
    setRemoving(true);
    setError(null);
    try {
      await floatingFormulaService.remove(enquiryId, { productId, side });
      onRemoved();
    } catch (err) {
      setError(serverSentence(err, 'The floating formula could not be removed. Please try again.'));
      setRemoving(false);
      setConfirmingRemove(false);
    }
  };

  const today = new Date();
  const months = monthOptions(today, [draft.periodMonth]);
  const pricePoints = pricePointsFor(selected?.default_bate, formula?.bate, draft.bate);
  const subtitle = [displayId, SIDE_LABEL[side], quantity || 'Quantity not entered yet']
    .filter(Boolean)
    .join(' · ');

  return createPortal(
    <div className="fef-modal-overlay">
      <div
        className="fef-modal ff-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ff-modal-title"
      >
        <header className="fef-modal-header ff-modal-header">
          <div>
            <h3 className="fef-modal-title" id="ff-modal-title">Floating-Price Formula</h3>
            <div className="ff-modal-subtitle">{subtitle}</div>
            {lineLabel && <div className="ff-modal-line">{lineLabel}</div>}
          </div>
          {/* Not plain "Close": that is also a price point in this window. */}
          <button
            type="button"
            className="fef-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close the formula window"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        <div className="fef-modal-body ff-modal-body">
          {!canEdit && (
            <div className="ff-warning" role="note">
              You can read this formula but not change it.
            </div>
          )}
          {!lineUnit && (
            <div className="ff-warning" role="note">
              This product line has no measuring unit yet. Pick one on the product
              card and save the enquiry: the formula needs it to know whether a
              conversion factor is needed.
            </div>
          )}

          <Step number={1} title="Benchmark and price point">
            <div className="fef-field">
              <label className="fef-label" htmlFor="ff-benchmark">
                Benchmark <span className="fef-label-required">*</span>
              </label>
              <select
                id="ff-benchmark"
                value={draft.benchmarkId}
                onChange={(e) => pickBenchmark(e.target.value)}
                disabled={locked}
              >
                <option value="">
                  {benchmarks.status === 'loading' ? 'Loading benchmarks…' : 'Pick a benchmark…'}
                </option>
                {benchmarks.list.map((b) => (
                  <option key={b.id} value={String(b.id)} disabled={b.supported === false}>
                    {benchmarkOptionText(b)}
                  </option>
                ))}
                {!savedIsListed && (
                  <option value={String(savedBenchmark.id)} disabled>
                    {`${savedBenchmark.code} — ${savedBenchmark.name} (switched off in the price list)`}
                  </option>
                )}
              </select>
              {benchmarks.status === 'failed' && (
                <div className="fef-field-error-text" role="alert">
                  The benchmark list could not be loaded: {benchmarks.error}{' '}
                  <button type="button" className="ff-link-btn" onClick={loadBenchmarks}>
                    <FontAwesomeIcon icon={faRotateRight} /> Try again
                  </button>
                </div>
              )}
            </div>
            <div className="fef-grid fef-grid-3">
              <ReadOnlyValue label="Benchmark code" value={selected?.code} mono />
              <ReadOnlyValue label="Location" value={selected?.location} />
              <ReadOnlyValue
                label="Quoted in"
                value={selected ? `${currency}/${benchmarkUnit}` : ''}
              />
            </div>
            <Segmented
              id="ff-bate-label"
              label="Price point"
              options={pricePoints}
              value={draft.bate}
              onChange={setField('bate')}
              disabled={locked}
            />
          </Step>

          <Step number={2} title="Pricing basis">
            <Segmented
              id="ff-option-label"
              label="How the price is set"
              options={PRICING_OPTIONS}
              value={draft.pricingOption}
              onChange={setField('pricingOption')}
              disabled={locked}
            />
            {draft.pricingOption === 'whole_month' && (
              <div className="fef-field ff-narrow">
                <label className="fef-label" htmlFor="ff-month">
                  Pricing month <span className="fef-label-required">*</span>
                </label>
                <select
                  id="ff-month"
                  value={draft.periodMonth}
                  onChange={(e) => setField('periodMonth')(e.target.value)}
                  disabled={locked}
                >
                  <option value="">Pick the month…</option>
                  {months.map((group) => (
                    <optgroup key={group.year} label={group.year}>
                      {group.months.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}
            {draft.pricingOption === 'single_day' && (
              <div className="fef-field ff-narrow">
                <label className="fef-label" htmlFor="ff-pricing-date">Delivery date</label>
                <DatePicker
                  id="ff-pricing-date"
                  name="pricing_date"
                  value={draft.pricingDate}
                  onChange={(e) => setField('pricingDate')(e.target.value || '')}
                  placeholder="Date to follow"
                  format="YYYY-MM-DD"
                  disabled={locked}
                />
                <div className="fef-field-hint">
                  Leave empty until the delivery date is known — the price stays provisional
                </div>
              </div>
            )}
            {draft.pricingOption === 'date_range' && (
              <div className="fef-grid fef-grid-2">
                <div className="fef-field">
                  <label className="fef-label" htmlFor="ff-period-start">
                    First pricing day <span className="fef-label-required">*</span>
                  </label>
                  <DatePicker
                    id="ff-period-start"
                    name="period_start"
                    value={draft.periodStart}
                    onChange={(e) => setField('periodStart')(e.target.value || '')}
                    placeholder="From"
                    format="YYYY-MM-DD"
                    disabled={locked}
                  />
                </div>
                <div className="fef-field">
                  <label className="fef-label" htmlFor="ff-period-end">
                    Last pricing day <span className="fef-label-required">*</span>
                  </label>
                  <DatePicker
                    id="ff-period-end"
                    name="period_end"
                    value={draft.periodEnd}
                    onChange={(e) => setField('periodEnd')(e.target.value || '')}
                    placeholder="To"
                    format="YYYY-MM-DD"
                    disabled={locked}
                  />
                </div>
              </div>
            )}
          </Step>

          <Step number={3} title="Price adjustment">
            <div className="ff-adjustment-row">
              <Segmented
                id="ff-sign-label"
                label="Premium or discount"
                options={SIGN_OPTIONS}
                value={draft.adjustmentSign}
                onChange={setField('adjustmentSign')}
                disabled={locked}
              />
              <div className="fef-field ff-narrow">
                <label className="fef-label" htmlFor="ff-adjustment">
                  Amount <span className="fef-label-required">*</span>
                </label>
                <AmountInput
                  id="ff-adjustment"
                  value={draft.adjustmentValue}
                  onChange={setField('adjustmentValue')}
                  maxDecimals={PRICE_DECIMALS}
                  placeholder="e.g. 8.00"
                  disabled={locked}
                />
                <div className="fef-field-hint">
                  {selected && lineUnit && !unitsDiffer
                    ? `${currency} per ${lineUnit}. Enter 0 if there is none.`
                    : 'Enter 0 if there is none.'}
                </div>
              </div>
            </div>
            {unitsDiffer && (
              <Segmented
                id="ff-adjustment-unit-label"
                label="The amount is"
                options={[
                  { value: 'benchmark', label: `Per ${benchmarkUnit} (added before converting)` },
                  { value: 'deal', label: `Per ${lineUnit} (added after converting)` },
                ]}
                value={draft.adjustmentUnit}
                onChange={setField('adjustmentUnit')}
                disabled={locked}
              />
            )}
          </Step>

          {unitsDiffer && (
            <Step number={4} title="Unit conversion">
              <div className="fef-grid fef-grid-3">
                <ReadOnlyValue label="Original benchmark unit" value={`${currency}/${benchmarkUnit}`} />
                <ReadOnlyValue label="Deal unit" value={`${currency}/${lineUnit}`} />
                <div className="fef-field">
                  <label className="fef-label" htmlFor="ff-factor">
                    Contract conversion factor ({benchmarkUnit} per {lineUnit}){' '}
                    <span className="fef-label-required">*</span>
                  </label>
                  <AmountInput
                    id="ff-factor"
                    value={draft.conversionFactor}
                    onChange={setField('conversionFactor')}
                    maxDecimals={4}
                    placeholder="From the contract"
                    aria-required="true"
                    disabled={locked}
                  />
                  <div className="fef-field-hint">From the contract. Hamster never guesses this.</div>
                </div>
              </div>
            </Step>
          )}

          <FormulaPreview preview={preview} />

          {error && <div className="fef-modal-error" role="alert">{error}</div>}
        </div>

        <footer className="fef-modal-footer ff-modal-footer">
          <p className="ff-saved-note">
            Saved with the enquiry: benchmark, price point, pricing basis, dates,
            adjustment, conversion factor and the result shown here.
          </p>
          {confirmingRemove ? (
            <div className="ff-modal-actions ff-remove-confirm" role="alert">
              <span className="ff-remove-question">
                Remove this formula? While this side stays Floating, the deal
                cannot move on without one.
              </span>
              <button
                type="button"
                className="fef-modal-btn fef-modal-btn-secondary"
                onClick={() => setConfirmingRemove(false)}
                disabled={busy}
              >
                Keep it
              </button>
              <button
                type="button"
                className="fef-modal-btn ff-danger-btn"
                onClick={handleRemove}
                disabled={busy}
              >
                {removing ? 'Removing…' : 'Remove formula'}
              </button>
            </div>
          ) : (
            <div className="ff-modal-actions">
              {formula && canEdit && (
                <button
                  type="button"
                  className="fef-modal-btn ff-remove-btn"
                  onClick={() => setConfirmingRemove(true)}
                  disabled={busy}
                >
                  Remove formula
                </button>
              )}
              <button
                type="button"
                className="fef-modal-btn fef-modal-btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              {canEdit && (
                <button
                  type="button"
                  className="fef-modal-btn fef-modal-btn-primary"
                  onClick={handleSave}
                  disabled={busy}
                >
                  {saving ? 'Saving…' : 'Save floating price'}
                </button>
              )}
            </div>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default FloatingFormulaModal;
